import { setProjectBaseUrl } from '@/shared/Config'
import Vue from 'vue'
import OrganizationAside from './OrganizationAside'

const components = {
  NoNewAccount: () => import('@/core/components/NoNewAccount/NoNewAccount'),
  Accounts: () => import('@/core/components/Account/Accounts'),
  ProjectDashboard: () => import('@/core/components/Dataplant/Dashboard/Dashboard'),
  DataplantDeletionConfirmation: () => import('@/core/components/Dataplant/DataplantDeletion/Confirmation'),
  Features: () => import('@/core/components/Features'),
  HomeNoOrganization: () => import('@/core/components/Home/NoOrganization'),
  HomeOrganization: () => import('@/core/components/Home/Organization'),
  HomeRedirect: () => import('@/core/components/Home/Redirect'),
  Teammates: () => import('@/core/components/Home/Teammates'),
  IframeService: () => import('@/core/components/IframeService'),
  OrganizationDataplants: () => import('@/core/components/Organization/Dataplants'),
  OrganizationOverview: () => import('@/core/components/Organization/Overview'),
  OrganizationMembers: () => import('@/core/components/Organization/Members'),
  OrganizationPlan: () => import('@/core/components/Organization/Plan'),
  OrganizationRedirect: () => import('@/core/components/Organization/Redirect'),
  OrganizationSettings: () => import('@/core/components/Organization/Settings'),
  Profile: () => import('@/core/components/Profile/Profile'),
  SupportOrganization: () => import('@/core/components/Support/Organization'),
  SupportRedirect: () => import('@/core/components/Support/Redirect'),
  SupportTicket: () => import('@/core/components/Support/Ticket'),
  Services: () => import('@/core/components/Home/Services/Services'),
  IframeCallback: () => import('@/auth/components/IframeCallback'),
  OrganizationQuotas: () => import('@/core/components/Organization/Quotas'),
  OrganizationAudit: () => import('@/core/components/Organization/Auditing')
}


const onDataplantIdReady = function () {
  const dataplant = this.$store.getters.DATAPLANT_BY_ID(this.$route.params.projectId || this.$route.params.dataplantId)
  if (dataplant) {
    // Set current dataplant urls
    setProjectBaseUrl(dataplant)
  }
}

const isReadyDataplantId = function () {
  // TODO: Remove old dataplantId
  const dataplant = this.$store.getters.DATAPLANT_BY_ID(this.$route.params.projectId || this.$route.params.dataplantId)
  return !!dataplant
}


const rawRoutes = [
  {
    path: '/iframe-callback',
    name: 'IframeCallback',
    component: components.IframeCallback,
    meta: {
      analytics: { category: 'authentication' }
    }
  },
  {
    path: '/home',
    name: 'HQ Home',
    component: components.HomeRedirect,
    meta: {
      analytics: { category: 'dashboard' },
      intercom: true,
      ready: ['core_organizations', 'core_accounts'],
      fullscreen: true
    }
  },
  {
    path: '/home/teammates',
    name: 'HQ Home Teammates',
    component: components.Teammates,
    meta: {
      analytics: { category: 'onboarding' },
      intercom: true,
      ready: ['core_organizations', 'core_accounts'],
      sidebar: false
    }
  },
  {
    path: '/home/new',
    name: 'HQ Home New',
    component: components.HomeNoOrganization,
    meta: {
      analytics: { category: 'onboarding' },
      intercom: true,
      ready: ['core_organizations', 'core_accounts']
    }
  },
  {
    path: '/home/:organizationId',
    name: 'HQ Home Organization',
    component: components.HomeOrganization,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'dashboard' },
      ready: ['core_organizations', 'core_accounts'],
      intercom: true
    }
  },
  {
    path: '/home/:organizationId/services',
    name: 'HQ Home Services',
    component: components.Services,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'dashboard' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations', 'core_accounts']
    }
  },
  {
    path: '/features',
    name: 'Features',
    component: components.Features,
    meta: {
      title: 'Features - Data Platform',
      ready: ['core_organizations']
    }
  },
  {
    path: '/support',
    name: 'Support',
    searchable: true,
    component: components.SupportRedirect,
    meta: {
      analytics: { category: 'support' },
      bordered: true,
      title: 'Support - Data Platform',
      ready: ['core_organizations'],
      sidebar: false
    }
  },
  {
    path: '/support/:organizationId',
    name: 'Support Organization',
    component: components.SupportOrganization,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'support' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      title: (route, parent) => `Support - ${parent(route)}`,
      bordered: true,
      sidebar: false
    }
  },
  {
    path: '/support/:organizationId/ticket',
    name: 'Support Create ticket',
    component: components.SupportTicket,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'support' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      title: (route, parent) => `Support - ${parent(route)}`,
      bordered: true,
      sidebar: false
    }
  },
  {
    path: '/support/:organizationId/ticket/:ticketId',
    name: 'Support Edit ticket',
    component: components.SupportTicket,
    props: (route) => ({ organizationId: route.params.organizationId, ticketId: route.params.ticketId }),
    meta: {
      analytics: { category: 'support' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      title: (route, parent) => {
        return `${route.params.ticketId} - Support - ${parent(route)}`
      },
      bordered: true,
      sidebar: false
    }
  },
  {
    path: '/project/:projectId',
    name: 'Project',
    searchable: true,
    component: components.ProjectDashboard,
    props: (route) => ({ projectId: route.params.projectId }),
    meta: {
      analytics: { category: 'onboarding' },
      async onMounted () {
        try {
          await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID_CASCADE', { dataplantId: this.$route.params.projectId })
        } catch (err) {
          console.error(err.stack)
          if (err.status === 404) {
            Vue.$router.push('/')
          }
        }
      },
      isReady: isReadyDataplantId,
      onReady: onDataplantIdReady,
      intercom: true,
      ready: []
    }
  },
  {
    path: '/project/:dataplantId/remove',
    name: 'Dataplant Remove',
    component: components.DataplantDeletionConfirmation,
    props: (route) => ({ dataplantId: route.params.dataplantId, token: route.query.tokenRemove, email: route.query.email }),
    meta: {
      async onMounted () {
        try {
          await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID_CASCADE', { dataplantId: this.$route.params.dataplantId })
        } catch (err) {
          console.error(err.stack)
          if (err.status === 404) {
            Vue.$router.push('/')
          }
        }
      },
      onReady: onDataplantIdReady,
      ready: ['core_organizations']
    }
  },
  {
    path: '/organizations',
    name: 'Organization Redirect',
    component: components.OrganizationRedirect,
    meta: {
      analytics: { category: 'dashboard' },
      ready: ['core_organizations'],
      fullscreen: true
    }
  },
  {
    path: '/organization/:organizationId',
    name: 'Organization Overview',
    component: components.OrganizationOverview,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'dashboard' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/organization/:organizationId/members',
    name: 'Organization Members',
    component: components.OrganizationMembers,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'listing' },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/organization/:organizationId/projects',
    name: 'Organization Projects',
    component: components.OrganizationDataplants,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'listing' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/organization/:organizationId/quotas',
    name: 'Organization Quotas',
    component: components.OrganizationQuotas,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'settings' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/organization/:organizationId/plan',
    name: 'Organization Plan',
    component: components.OrganizationPlan,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'settings' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations', 'core_accounts', 'core_prices'],
      aside: OrganizationAside,
      bordered: true,
      legacy: true
    }
  },
  {
    path: '/organization/:organizationId/settings',
    name: 'Organization Settings',
    component: components.OrganizationSettings,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'settings' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/organization/:organizationId/auditing',
    name: 'Organization Auditing',
    component: components.OrganizationAudit,
    props: (route) => ({ organizationId: route.params.organizationId }),
    meta: {
      analytics: { category: 'listing' },
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_ORGANIZATION_ID', { organizationId: this.$route.params.organizationId })
      },
      ready: ['core_organizations'],
      aside: OrganizationAside,
      bordered: true
    }
  },
  {
    path: '/qb/:dataplantId',
    name: 'Query Builder',
    component: components.IframeService,
    props: (route) => ({ dataplantId: route.params.dataplantId, service: 'qb' }),
    meta: {
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID', { dataplantId: this.$route.params.dataplantId })
      },
      onReady: onDataplantIdReady,
      title: (route, parent) => `QB - ${parent(route)}`,
      ready: ['core_organizations'],
      theme: 'v3'
    }
  },
  {
    path: '/app/:dataplantId/:appName',
    name: 'App Manager',
    component: components.IframeService,
    props: (route) => ({ dataplantId: route.params.dataplantId, service: 'appservice', query: { appName: route.params.appName, appType: 'app' } }),
    meta: {
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID', { dataplantId: this.$route.params.dataplantId })
      },
      onReady: onDataplantIdReady,
      title: (route, parent) => `APP Manager - ${parent(route)}`,
      ready: ['core_organizations'],
      theme: 'v3'
    }
  },
  {
    path: '/api/:dataplantId/:appName',
    name: 'API Manager',
    component: components.IframeService,
    props: (route) => ({ dataplantId: route.params.dataplantId, service: 'appservice', query: { appName: route.params.appName, appType: 'api' } }),
    meta: {
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID', { dataplantId: this.$route.params.dataplantId })
      },
      onReady: onDataplantIdReady,
      title: (route, parent) => `API Manager - ${parent(route)}`,
      ready: ['core_organizations'],
      theme: 'v3'
    }
  },
  {
    path: '/superset/:dataplantId/:appName',
    name: 'Apache Superset Manager',
    component: components.IframeService,
    props: (route) => ({ dataplantId: route.params.dataplantId, service: 'appservice', query: { appName: route.params.appName, appType: 'superset' } }),
    meta: {
      async onMounted () {
        await this.$store.dispatch('REFRESH_DATAPLANT_BY_DATAPLANT_ID', { dataplantId: this.$route.params.dataplantId })
      },
      onReady: onDataplantIdReady,
      title: (route, parent) => `Apache Superset Manager - ${parent(route)}`,
      ready: ['core_organizations'],
      theme: 'v3'
    }
  },
  {
    path: '/user',
    name: 'Profile',
    display_name: 'searchbar.items.core.profile.display_name',
    description: 'searchbar.items.core.profile.description',
    searchable: true,
    component: components.Profile,
    meta: {
      analytics: { category: 'account' },
      title: 'Profile - Data Platform',
      ready: ['core_organizations']
    }
  },
  {
    path: '/accounts',
    name: 'Accounts',
    component: components.Accounts,
    meta: {
      analytics: { category: 'account' },
      title: 'Accounts - Data Platform',
      ready: ['core_accounts', 'core_organizations'],
      legacy: true
    }
  },
  {
    path: '/no_new_account',
    name: 'No new account',
    component: components.NoNewAccount,
    meta: {
      analytics: { category: 'account' },
      title: 'No New Account - Data Platform',
      fullscreen: true
    }
  },
  {
    path: '/:pathMatch(.*)',
    redirect: { name: 'HQ Home' }
  }
]

export default rawRoutes
